import React, { useLayoutEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from '@mui/material';
import { useGetRepositoryByIdQuery } from '../services/repositoryEntries';
import { EntityType, type QueryEntry, type Repository } from '../types';
import ChartWhereFilterBuilder from './ChartWhereFilterBuilder';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { selectAllPropertyDefinitions } from '../containers/contentSlice';
import { sortEntryByRank } from '../common/util';

export interface EditQueryDialogProps {
	open: boolean;
	query: QueryEntry | null;
	repositories: Repository[];
	onClose: () => void;
	onSubmit: (payload: {
		id: string;
		repositoryId: string;
		title: string;
		where?: string;
		includeArchived: boolean;
	}) => void;
}

const EditQueryDialog: React.FC<EditQueryDialogProps> = ({
	open,
	query,
	repositories,
	onClose,
	onSubmit,
}) => {
	const [repositoryId, setRepositoryId] = useState('');
	const [title, setTitle] = useState('');
	const [where, setWhere] = useState('');
	const [includeArchived, setIncludeArchived] = useState(true);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);

	const { data: repositoryById, isFetching: isRepositoryFetching } = useGetRepositoryByIdQuery(
		repositoryId,
		{ skip: !open || !repositoryId || !isAuthenticated }
	);
	const localPropertyDefinitions = useMemo(
		() =>
			allPropertyDefinitions.filter((def) =>
				def.parentId === repositoryId
			).sort(sortEntryByRank),
		[allPropertyDefinitions, repositoryId]
	);
	const propertyDefinitionsSource = useMemo(
		() =>
			(isAuthenticated ? (repositoryById?.propertyDefinitions ?? []) : localPropertyDefinitions) as Array<{
				slug: string;
				label?: string;
				type: EntityType;
				required?: boolean;
				targetRepositoryId?: string;
			}>,
		[isAuthenticated, repositoryById?.propertyDefinitions, localPropertyDefinitions]
	);
	const propertyOptions = useMemo(
		() =>
			propertyDefinitionsSource
				.map((property) => ({
					slug: property.slug,
					label: property.label?.trim() ? property.label : property.slug,
					type: property.type,
					required: (property as { required?: boolean }).required === true,
					targetRepositoryId:
						property.type === EntityType.PropertyDefinitionBelongsTo
							? String(
								(property as { targetRepositoryId?: string }).targetRepositoryId ?? '',
							).trim()
							: '',
				})),
		[propertyDefinitionsSource]
	);
	useLayoutEffect(() => {
		if (!open || !query) {
			return;
		}
		setRepositoryId(query.repositoryId ?? repositories[0]?.id ?? '');
		setTitle(query.title ?? '');
		setWhere(query.where ?? '');
		setIncludeArchived(query.includeArchived !== false);
	}, [open, query, repositories]);

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!query) {
			return;
		}
		const titleTrimmed = title.trim();
		const whereTrimmed = where.trim();
		if (!repositoryId || !titleTrimmed) {
			return;
		}
		onSubmit({
			id: query.id,
			repositoryId,
			title: titleTrimmed,
			where: whereTrimmed || undefined,
			includeArchived,
		});
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="body">
			<form onSubmit={handleSubmit} autoComplete="off">
				<DialogTitle>Edit query</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={1}>
						<TextField
							label="Title"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							fullWidth
							required
							autoFocus
							size="small"
						/>
						<FormControl fullWidth required size="small">
							<InputLabel id="edit-query-repository-label">Collection</InputLabel>
							<Select
								labelId="edit-query-repository-label"
								label="Collection"
								value={repositoryId}
								onChange={(e) => {
									setRepositoryId(String(e.target.value));
									setWhere('');
									setIncludeArchived(true);
								}}
							>
								{repositories.map((r) => (
									<MenuItem key={r.id} value={r.id}>
										{r.title}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{query && open && (
							<ChartWhereFilterBuilder
								chartId={query.id}
								initialWhere={where}
								onChange={setWhere}
								properties={propertyOptions}
								disabled={!repositoryId || isRepositoryFetching}
								includeArchived={includeArchived}
								onIncludeArchivedChange={setIncludeArchived}
								shouldAskAboutRelativeTime={true}
							/>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button type="button" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={!repositoryId || !title.trim()}>
						Save
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default EditQueryDialog;
